import { useQuery } from "@oysterhr/core"
import type {
  Company,
  CountryIntelligenceOysterScore,
  CountryIsoCode,
  Employment,
  Engagement,
  InvoicingCompanySettings,
  Maybe,
} from "@oysterhr/schema"
import {
  HIRING_FORM_EMPLOYMENT_FIELDS_FRAGMENT,
  type HiringFormEmploymentFields,
} from "~/app/fragments/HiringFormEmploymentFields"
import type { EmploymentTemplateFragment } from "~/shared/fragments/EmploymentTemplateFragment"
import { gql } from "@apollo/client"
import { PROVISIONED_ENROLLMENT_COST_CONTRIBUTIONS_FRAGMENT } from "~/shared/fragments/ProvisionedEnrollmentCostContributionsFragment"
import { PLAN_CONTRIBUTION_SETTINGS_FRAGMENT } from "~/shared/fragments/PlanContributionSettingFragment"
import { MONEY_FIELDS } from "~/shared/fragments/MoneyFields"
import { MaybeMergePick, MergeDeep } from "~/app/utils/typeUtils"
import {
  COMPANY_SIGNATORY_FRAGMENT,
  EMPLOYMENT_SIGNATORY_FRAGMENT,
} from "../../employment-agreements/components/AgreementSignerModal/fragments"
import {
  CompanySignatoryFragmentFragment,
  EmploymentSignatoryFragmentFragment,
} from "../../../gql/graphql"

export const HIRING_FTE_COMPANY_GET_COMPANY_EMPLOYMENT_ENGAGEMENT = gql`
  ${MONEY_FIELDS}
  ${HIRING_FORM_EMPLOYMENT_FIELDS_FRAGMENT}
  ${COMPANY_SIGNATORY_FRAGMENT}
  ${EMPLOYMENT_SIGNATORY_FRAGMENT}
  ${PLAN_CONTRIBUTION_SETTINGS_FRAGMENT}
  ${PROVISIONED_ENROLLMENT_COST_CONTRIBUTIONS_FRAGMENT}
  query HiringFTECompanyGetCompanyEmploymentEngagement(
    $companyId: ID!
    $employmentId: ID!
    $skipEmployment: Boolean!
    $employmentTemplateId: ID
    $countryCode: CountryIsoCode
    $skipEmploymentTemplate: Boolean!
    $oysterScore: CountryIntelligenceOysterScore
  ) {
    employment(employmentId: $employmentId) @skip(if: $skipEmployment) {
      ...HiringFormEmploymentFields
      ...EmploymentSignatoryFragment
      company {
        ...CompanySignatoryFragment
        invoicingSettings {
          id
          monthsOfDeposit
        }
        depositInvoiceRequired
      }
      employmentTemplate {
        ...EmploymentTemplateFragment
      }
    }
    engagement(engagementId: $employmentId) @skip(if: $skipEmployment) {
      authorizations {
        actions
      }
      id
      engagementType
      benefitsDescription
      payrollCurrencyCode
      benefitsEnrolledPackages {
        collection {
          tier {
            id
          }
        }
      }
      pendingOrActiveSubscription {
        oldPlan {
          id
          product
          category
        }
      }
      benefitsProvisionedEnrollments {
        collection {
          ...ProvisionedEnrollmentCostContributionsFragment
          dependentsEmployerContribution
          dependentsEmployerContributionMaxCount
          employerContribution
          plan {
            id
            name
            category
            ...PlanContributionSettingFragment
            provider {
              id
              name
            }
          }
        }
      }
      benefitsEnrolledPackages {
        collection {
          tier {
            id
          }
        }
      }
      benefitsProvisionedAddons {
        collection {
          id
          category
        }
      }
      equityGrants {
        id
        status
        grantDate
        equityType
        grantShares
        equitySubType
        grantAmount {
          ...MoneyFields
        }
        vestingStartDate
        vestingCliffMonths
        vestingCliffShares
        vestingScheduleMonths
      }
    }
    company(companyId: $companyId) {
      id
      name
      countryCode
      paymentCurrencyCode
      directDebitAvailable
      directDebitAuthorized
      departments {
        id
        name
      }
      equityCompanyPlan {
        id
        vestingCliffMonths
        vestingCliffShares
        vestingScheduleMonths
      }
      equityCompanyValuation {
        id
        valuation {
          ...MoneyFields
        }
      }
      equityCartaIntegration {
        id
        lastSyncedAt
      }
      authorizations {
        actions
      }
    }
    employmentTemplate(
      id: $employmentTemplateId
      companyId: $companyId
      countryCode: $countryCode
      oysterScore: $oysterScore
    ) @skip(if: $skipEmploymentTemplate) {
      ...EmploymentTemplateFragment
    }
  }
`

// @todo/codegen: Remove this type once we have codegen for this query
export type HiringFTECompanyGetCompanyEmploymentEngagementQuery = {
  employment: MergeDeep<
    MergeDeep<HiringFormEmploymentFields, EmploymentSignatoryFragmentFragment>,
    {
      company?: MaybeMergePick<
        Company,
        "depositInvoiceRequired",
        MergeDeep<
          {
            employmentTemplate: EmploymentTemplateFragment
            invoicingSettings?: Pick<InvoicingCompanySettings, "id" | "monthsOfDeposit">
          },
          CompanySignatoryFragmentFragment
        >
      >
    }
  >

  engagement: Pick<
    Engagement,
    | "id"
    | "authorizations"
    | "engagementType"
    | "benefitsEnrolledPackages"
    | "benefitsProvisionedAddons"
    | "payrollCurrencyCode"
    | "pendingOrActiveSubscription"
  >
  company: Pick<
    Company,
    | "id"
    | "name"
    | "directDebitAuthorized"
    | "directDebitAvailable"
    | "paymentCurrencyCode"
    | "departments"
    | "equityCompanyPlan"
    | "countryCode"
  >
  employmentTemplate?: Maybe<EmploymentTemplateFragment>
}

export const useHiringFTEFormPageQuery = ({
  employmentId,
  companyId,
  templateId,
  countryCode,
  oysterScore,
}: {
  employmentId: string | null
  companyId: string
  templateId: string | null
  countryCode?: CountryIsoCode
  oysterScore?: CountryIntelligenceOysterScore
}) => {
  // Oyster Templates have a null ID, but have countryCode and oysterScore.
  const skipEmploymentTemplate =
    (Boolean(employmentId) || !templateId) && !(countryCode && oysterScore)
  const { data, loading } = useQuery<
    HiringFTECompanyGetCompanyEmploymentEngagementQuery,
    {
      employmentId: string
      skipEmployment: boolean
      companyId: string
      employmentTemplateId: string | null
      skipEmploymentTemplate: boolean
      countryCode?: CountryIsoCode
      oysterScore?: CountryIntelligenceOysterScore
    }
  >(HIRING_FTE_COMPANY_GET_COMPANY_EMPLOYMENT_ENGAGEMENT, {
    variables: {
      employmentId: employmentId || "",
      skipEmployment: !employmentId,
      companyId: companyId,
      employmentTemplateId: templateId || null,
      skipEmploymentTemplate,
      countryCode: countryCode,
      oysterScore: oysterScore,
    },
  })

  // Derived data
  // @todo: remove the type assertion in a future PR
  const company = data?.company as Company
  const engagement = data?.engagement as unknown as Engagement
  const employment = data?.employment as unknown as Employment

  return {
    data: {
      company,
      engagement,
      employment,
      employmentTemplate: data?.employment?.employmentTemplate || data?.employmentTemplate,
    },
    loading,
  }
}
